import { Injectable, inject } from '@angular/core';
import { MessageService } from 'primeng/api';
import { TranslocoService } from '@jsverse/transloco';

/**
 * Single entry point for user-facing toasts. Translates i18n keys via transloco
 * and de-duplicates identical messages fired within a short window, so a single
 * failure surfaced by BOTH an HTTP error interceptor and an NgRx failure effect
 * does not stack two identical toasts.
 *
 * Transloco is injected optionally: the admin app has no transloco setup, so
 * there the key is shown as-is (no worse than the previous behavior) instead of
 * throwing.
 */
@Injectable({ providedIn: 'root' })
export class NotificationService {
  private readonly messageService = inject(MessageService);
  private readonly transloco = inject(TranslocoService, { optional: true });

  /** Dedupe window in ms — the same message within this window is ignored. */
  private static readonly DEDUPE_MS = 1500;
  private lastKey: string | null = null;
  private lastAt = 0;

  /**
   * Show an error toast for an i18n key (or raw message). Deduped + translated.
   *
   * @param params  interpolation params for the i18n key (e.g. `{ permission }`).
   * @param dedupeKey  overrides the string used for de-duplication. Pass a stable
   *   value so two DIFFERENT messages describing the SAME failure (e.g. a generic
   *   `errors.forbidden` from an NgRx effect and a specific `errors.forbiddenWith`
   *   from the 403 interceptor) collapse into one toast.
   */
  error(
    keyOrMessage: string,
    params?: Record<string, unknown>,
    summaryKey = 'common.error',
    dedupeKey?: string,
  ): void {
    if (this.isDuplicate(dedupeKey ?? keyOrMessage)) return;
    this.messageService.add({
      severity: 'error',
      summary: this.translate(summaryKey, 'Error'),
      detail: this.translate(keyOrMessage, keyOrMessage, params),
      life: 5000,
    });
  }

  /** Show a success toast for an i18n key (or raw message). Deduped + translated. */
  success(keyOrMessage: string, summaryKey = 'common.success'): void {
    if (this.isDuplicate(keyOrMessage)) return;
    this.messageService.add({
      severity: 'success',
      summary: this.translate(summaryKey, 'Success'),
      detail: this.translate(keyOrMessage, keyOrMessage),
      life: 4000,
    });
  }

  private translate(
    key: string,
    fallback: string,
    params?: Record<string, unknown>,
  ): string {
    if (!this.transloco) return fallback;
    const translated = this.transloco.translate(key, params);
    // transloco returns the key unchanged when it has no translation for it.
    return translated === key ? fallback : translated;
  }

  private isDuplicate(key: string): boolean {
    const now = Date.now();
    if (key === this.lastKey && now - this.lastAt < NotificationService.DEDUPE_MS) {
      return true;
    }
    this.lastKey = key;
    this.lastAt = now;
    return false;
  }
}
